import { useState } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useLocationSitemapPageData } from 'src/app/pages/locationSitemap/pageContext';
import { useQueryParamValue, useUpdateQueryString } from 'src/app/shared/hooks/location';
import { usStatesMap } from 'src/app/shared/utils/unitedStatesMapping';

type Filter = {
	name: string;
	selected: boolean;
	value: string;
};

type Result = ModelHookResult<
	{
		hasSelectedFilters: boolean;
		visibleFilters: Filter[];
		shouldShowToggle: boolean;
		showFullList: boolean;
	},
	{
		updateFilter: (name: string) => void;
		clearFilters: () => void;
		toggleList: () => void;
	}
>;

export function useStateCodeFilter(): Result {
	const [showFullList, setShowFullList] = useState(false);

	const filterQueryParam = useQueryParamValue('state_code') || '';
	const updateQueryParams = useUpdateQueryString();
	const trackEvent = useTracking();
	const {
		actions: { refreshData },
	} = useLocationSitemapPageData();

	const filters = Object.entries(usStatesMap).map(([value, name]) => ({
		name,
		selected: filterQueryParam === value,
		value,
	}));
	const hasSelectedFilters = filterQueryParam.length > 0;

	// Arbitrary number to show initial list of filters to hide the
	// rest behind a "Show more" toggle
	const maxVisibleFilters = 25;
	const visibleFilters = showFullList ? filters : filters.slice(0, maxVisibleFilters);

	const updateFilter = (value: string) => {
		void trackEvent('location_sitemap_filters_click');
		updateQueryParams({ state_code: value }, { replace: true });
		refreshData();
	};

	const clearFilters = () => {
		void trackEvent('location_sitemap_clear_filters_click');
		updateQueryParams({ state_code: undefined }, { replace: true });
		refreshData();
	};

	const toggleList = () => {
		setShowFullList((prev) => !prev);
	};

	return {
		data: {
			hasSelectedFilters,
			visibleFilters,
			shouldShowToggle: filters.length > maxVisibleFilters,
			showFullList,
		},
		actions: {
			updateFilter,
			clearFilters,
			toggleList,
		},
	};
}
